import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Users, Truck, TrendingUp, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get('/super-admin/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
          Flotto Platform
        </h1>
        <p className="text-zinc-400">Super Admin Dashboard - Zonik Solutions</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Gesamt Tenants</CardTitle>
            <Building2 className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.total_tenants || 0}</div>
            <div className="flex gap-4 mt-3 text-xs">
              <div className="text-green-400">
                <CheckCircle className="h-3 w-3 inline mr-1" />
                {stats?.active_tenants || 0} aktiv
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Gesamt Benutzer</CardTitle>
            <Users className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.total_users || 0}</div>
            <p className="text-xs text-zinc-500 mt-3">Über alle Tenants</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Gesamt Fahrzeuge</CardTitle>
            <Truck className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.total_vehicles || 0}</div>
            <p className="text-xs text-zinc-500 mt-3">In der Platform</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Umsatz (MRR)</CardTitle>
            <TrendingUp className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">€0</div>
            <p className="text-xs text-zinc-500 mt-3">Monatlicher Umsatz</p>
          </CardContent>
        </Card>
      </div>

      {/* Tenants by Plan */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-white">Tenants nach Abo-Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats?.tenants_by_plan && Object.entries(stats.tenants_by_plan).map(([plan, count]) => (
              <div key={plan} className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-400">
                      {plan === 'starter' && '🟢 STARTER'}
                      {plan === 'business' && '🔵 BUSINESS'}
                      {plan === 'enterprise' && '🟣 ENTERPRISE'}
                    </p>
                    <p className="text-2xl font-bold text-white mt-1">{count}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-500">
                      {plan === 'starter' && '9,90€/Fzg'}
                      {plan === 'business' && '19,90€/Fzg'}
                      {plan === 'enterprise' && '29,90€/Fzg'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => window.location.href = '/super-admin/tenants'}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-blue-600/30 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h3 className="font-bold text-white">Tenants Verwalten</h3>
                <p className="text-sm text-zinc-400">Alle Kunden verwalten</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => window.location.href = '/register'}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-green-600/30 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-bold text-white">Neuer Tenant</h3>
                <p className="text-sm text-zinc-400">Kunde manuell hinzufügen</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border-purple-700 cursor-pointer hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-purple-600/30 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <h3 className="font-bold text-white">Abrechnungen</h3>
                <p className="text-sm text-zinc-400">Rechnungen & Zahlungen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
